/**
 * Injection seam for `@heidi/answer-kit`.
 *
 * ONE MEMBER, AND IT EXISTS BECAUSE OF ONE ANCHOR. The markdown vocabulary this
 * package holds is neutral apart from `inlineMdComponents.a`, which intercepts
 * an href naming an in-app destination and opens it in place instead of in a new
 * tab. App-side that interception read `ROUTE_TO_SLUG` /
 * `SETTINGS_QUERY_PARAM` from `@/app-shell/settings/atoms/settingsModalSlugs` and
 * pushed the `?settings=` param onto the current URL — a settings-modal
 * protocol the kit neither owns nor can name.
 *
 * So the resolution is INJECTED. `resolveInternalHref` answers a single
 * question — "does this href open in place, and if so how?" — as a handler or
 * `undefined`, which keeps the settings vocabulary, the query param and the
 * history push entirely app-side (`src/utils/answer-kit-wiring.ts`).
 *
 * IT DEFAULTS TO `() => undefined` RATHER THAN FAILING LOUD. An unconfigured
 * read renders the link as an external one, which is what every href that is
 * not an in-app route already does — so a missing bootstrap costs in-place
 * navigation on a handful of links and nothing else. That is the right failure
 * mode for a renderer, and it is also invisible, so both states are asserted:
 * unconfigured beside this file, configured in the app suite that imports the
 * wiring.
 *
 * `configureAnswerKit` takes a PARTIAL and merges, so a caller that only cares
 * about one member never has to restate the rest.
 */
export interface AnswerKitConfig {
  /**
   * Resolve an href the app can open in place.
   *
   * Returns the handler to run instead of following the link, or `undefined`
   * when the href is not an in-app destination. A handler rather than a boolean
   * so the kit never learns what "in place" means for a given href.
   */
  resolveInternalHref: (href: string) => (() => void) | undefined;
}

const DEFAULTS: AnswerKitConfig = {
  resolveInternalHref: () => undefined,
};

let current: AnswerKitConfig = DEFAULTS;

/**
 * One-time app-bootstrap call (`src/utils/answer-kit-wiring.ts`, imported at
 * module scope from `_app.tsx`).
 */
export function configureAnswerKit(config: Partial<AnswerKitConfig>): void {
  current = { ...current, ...config };
}

/**
 * Read the injected resolver. A GETTER rather than a captured module constant so
 * wiring order can never race a module-scope consumer: the anchor renderers are
 * defined at module scope and read this at RENDER time, so they see whatever is
 * current then.
 */
export function resolveInternalHref(href: string): (() => void) | undefined {
  return current.resolveInternalHref(href);
}

/** Test-only reset so a suite cannot leak a double into the next one. */
export function __resetAnswerKitForTests(): void {
  current = DEFAULTS;
}
